import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChartSkeleton } from '@/components/ui/skeleton';
import { TrendingUp, RefreshCw, AlertCircle } from 'lucide-react';
import type { FaceRecognitionVolume } from '../../api/face-recognition.schemas';

interface VolumeOverTimeChartProps {
  data?: FaceRecognitionVolume;
  loading?: boolean;
  error?: Error;
  className?: string;
  onRetry?: () => void;
}

/**
 * VolumeOverTimeChart Component
 * 
 * Renders a line chart showing Face Recognition volume trends over time using ECharts.
 * Displays time series data with smooth curves and area fill.
 * 
 * Requirements: 2.3, 6.2
 */
export function VolumeOverTimeChart({ 
  data, 
  loading = false, 
  error, 
  className = "",
  onRetry
}: VolumeOverTimeChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // Initialize chart instance if it doesn't exist
    if (!chartInstance.current) {
      console.log('📊 Initializing ECharts instance for VolumeOverTimeChart');
      chartInstance.current = echarts.init(chartRef.current);
    }

    const chart = chartInstance.current;

    // If loading, show loading state but don't clear the chart
    if (loading) {
      console.log('⏳ VolumeOverTimeChart: Showing loading state');
      chart.showLoading({
        text: 'Loading...',
        color: '#3b82f6',
        textColor: '#64748b',
        maskColor: 'rgba(255, 255, 255, 0.8)',
        zlevel: 0,
      });
      return;
    }

    // Hide loading state
    chart.hideLoading();

    // If error or no data, clear the chart
    if (error || !data || !data.byTime || data.byTime.length === 0) {
      console.log('❌ VolumeOverTimeChart: Clearing chart due to error or no data', { error, hasData: !!data });
      chart.clear();
      return;
    }

    console.log('📈 VolumeOverTimeChart: Rendering chart with data', { dataPoints: data.byTime.length });

    // Prepare chart data
    const labels = data.byTime.map(item => {
      const date = new Date(item.timestamp);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: data.byTime.length <= 24 ? 'numeric' : undefined,
        minute: data.byTime.length <= 24 ? '2-digit' : undefined,
      });
    });

    const values = data.byTime.map(item => item.count);

    // Configure chart options for time series
    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985',
          },
        },
        formatter: (params: any) => {
          if (Array.isArray(params) && params.length > 0) {
            const param = params[0];
            return `${param.name}<br/>${param.seriesName}: ${param.value.toLocaleString()}`;
          }
          return '';
        },
      },
      legend: {
        data: ['Completed Operations'],
        top: 10,
      },
      grid: {
        left: '3%',
        right: '4%',
        top: '15%',
        bottom: labels.length > 14 ? '15%' : '10%', // More space for rotated labels
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLabel: {
          rotate: labels.length > 14 ? 45 : 0, // Only rotate if more than 14 points
          interval: labels.length > 31 ? 'auto' : 0, // Show all labels for short periods
        },
      },
      yAxis: {
        type: 'value',
        name: 'Volume',
        nameLocation: 'middle',
        nameGap: 50,
        axisLabel: {
          formatter: (value: number) => {
            if (value >= 1000000) {
              return `${(value / 1000000).toFixed(1)}M`;
            } else if (value >= 1000) {
              return `${(value / 1000).toFixed(1)}K`;
            }
            return value.toString();
          },
        },
      },
      series: [
        {
          name: 'Completed Operations',
          type: 'line',
          stack: 'Total',
          smooth: true,
          lineStyle: {
            width: 2,
          },
          showSymbol: false,
          areaStyle: {
            opacity: 0.3,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(59, 130, 246, 0.5)',
              },
              {
                offset: 1,
                color: 'rgba(59, 130, 246, 0.1)',
              },
            ]),
          },
          emphasis: {
            focus: 'series',
          },
          data: values,
          itemStyle: {
            color: '#3b82f6',
          },
        },
      ],
      responsive: true,
    };

    // Set the option with notMerge: true to ensure clean update
    chart.setOption(option, { notMerge: true });
    console.log('✅ VolumeOverTimeChart: Chart updated successfully');

    // Handle resize
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, loading, error]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <Card className={`h-full flex flex-col ${className}`}>
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Volume Over Time</CardTitle>
          </div>
          <CardDescription>Face Recognition volume trends</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 p-6 pt-0 min-h-0">
          <ChartSkeleton height={300} showLegend={true} />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`h-full flex flex-col ${className}`}>
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Volume Over Time</CardTitle>
          </div>
          <CardDescription>Failed to load volume trends</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Unable to display chart</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
            {onRetry && (
              <Button onClick={onRetry} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.byTime || data.byTime.length === 0) {
    return (
      <Card className={`h-full flex flex-col ${className}`}>
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Volume Over Time</CardTitle>
          </div>
          <CardDescription>No volume data available</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <TrendingUp className="h-6 w-6 mx-auto mb-2" />
            <p>No data to display</p>
            <p className="text-sm">Volume trends will appear when data is available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`h-full flex flex-col ${className}`}>
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Volume Over Time</CardTitle>
        </div>
        <CardDescription>
          Completed operations over time ({data.total.toLocaleString()} total)
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 p-6 pt-0 min-h-0">
        <div 
          ref={chartRef} 
          style={{ height: '100%', width: '100%' }}
          className="min-h-0"
        />
      </CardContent>
    </Card>
  );
}